import isEqual from 'lodash.isequal';
import TagId from '~/constants/common/TagId';
import type {
  Facet,
  FacetData,
  FacetRanges,
  ListItem,
  MakeFacet,
  MakesFacet
} from '~/contracts/common';
import purgeEmpty from '~/utilities/purgeEmpty';

function isMakesFacet(_: Facet, key: string): _ is MakesFacet {
  return key === 'makes';
}

function mergeMakesFromBaseline(
  facetData: Partial<Omit<FacetData, 'pagination'>>,
  baselineMakes: MakesFacet
): Partial<FacetData> {
  return Object.fromEntries(
    Object.entries(facetData).map<[string, Facet]>(([facetKey, facet]) => {
      if (!isMakesFacet(facet, facetKey)) {
        return [facetKey, facet];
      }

      // Populate parent models from baseline if it is not returned from the API, i.e. filters included `models`
      // instead of `parentModels`, which tells the API to return `models` instead of `parentModels`.
      const makes = Object.fromEntries(
        Object.entries(facet).map<[string, MakeFacet]>(
          ([makeKey, { models, parentModels, ...make }]) => [
            makeKey,
            {
              ...make,
              models,
              parentModels:
                (make.count > 0 ? purgeEmpty(parentModels) : parentModels) ??
                baselineMakes[makeKey]?.parentModels ??
                []
            }
          ]
        )
      );

      return [facetKey, makes];
    })
  );
}

/**
 * When no results are found, range filters are not returned from the API. This function replaces them with baseline's
 * filters.
 */
function mergeRangesFromBaseline(
  facetData: Partial<FacetData>,
  baseline: Partial<FacetData>
): Partial<FacetData> {
  const rangeFacetKeys: Array<keyof FacetRanges> = [
    'mileage',
    'mpg',
    'price',
    'seatingCapacity',
    'towingCapacity',
    'year'
  ];

  return rangeFacetKeys.reduce<Partial<FacetData>>(
    (acc, key) => {
      acc[key] = acc[key] ?? baseline[key];

      return acc;
    },
    { ...facetData }
  );
}

export function mergeFromBaseline(
  facetData: Partial<FacetData>,
  baseline: Partial<FacetData>
): Partial<FacetData> {
  return mergeRangesFromBaseline(
    mergeMakesFromBaseline(facetData, baseline.makes ?? {}),
    baseline
  );
}

export function setPriceDropDisplayName(
  facetData: Partial<FacetData>,
  displayName: string
): Partial<FacetData> {
  const priceDropTag = facetData?.tagIds?.buckets?.find(
    facet => facet.key === TagId.PriceDrop.toString()
  );

  if (!priceDropTag) return facetData;

  const newPriceDropTag: ListItem = {
    ...priceDropTag,
    displayName
  };

  const newFacetData = { ...facetData };

  if (newFacetData.tagIds) {
    newFacetData.tagIds = {
      ...newFacetData.tagIds,
      buckets: newFacetData.tagIds.buckets.map(facet =>
        facet.key === TagId.PriceDrop.toString() ? newPriceDropTag : facet
      )
    };
  }

  if (newFacetData.waysToSave?.tagIds) {
    newFacetData.waysToSave = {
      ...newFacetData.waysToSave,
      tagIds: {
        ...newFacetData.waysToSave.tagIds,
        buckets: newFacetData.waysToSave.tagIds.buckets.map(facet =>
          facet.key === TagId.PriceDrop.toString() ? newPriceDropTag : facet
        )
      }
    };
  }

  return isEqual(newFacetData, facetData) ? facetData : newFacetData;
}
